import React, { useState, useEffect } from 'react';
import { useSidebar } from '../contexts/SidebarContext';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { apiService } from '../services/api';

interface AdminSidebarProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  activeSubTab?: string;
  onSubTabChange?: (subTab: string) => void;
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({ activeTab, onTabChange, activeSubTab, onSubTabChange }) => {
  const { isCollapsed } = useSidebar();
  const { user } = useAuth();
  const { darkMode } = useTheme();
  const [expandedAccess, setExpandedAccess] = useState(true);
  const [expandedContent, setExpandedContent] = useState(false);
  const [userScopes, setUserScopes] = useState<string[]>([]);
  const [scopesLoading, setScopesLoading] = useState(true);

  useEffect(() => {
    if (activeTab === 'access') {
      setExpandedAccess(true);
      setExpandedContent(false);
    } else if (activeTab === 'content') {
      setExpandedAccess(false);
      setExpandedContent(true);
    } else {
      setExpandedAccess(false);
      setExpandedContent(false);
    }
  }, [activeTab]);

  // Load user scopes on mount - use scopes from user object if available, otherwise fetch from API
  useEffect(() => {
    const loadUserScopes = async () => {
      try {
        setScopesLoading(true);
        // First try to use scopes from user object (from /me endpoint)
        if (user?.scopes && user.scopes.length > 0) {
          setUserScopes(user.scopes);
          setScopesLoading(false);
          return;
        }
        
        // Fallback: fetch scopes from API if not in user object
        const scopes = await apiService.getCurrentUserScopes();
        setUserScopes(scopes);
      } catch (error) {
        console.error('Error loading user scopes:', error);
        setUserScopes([]);
      } finally {
        setScopesLoading(false);
      }
    };
    loadUserScopes();
  }, [user]);

  const accessSubTabs = [
    { id: 'users', label: 'Users', icon: 'fa-user' },
    { id: 'groups', label: 'Groups', icon: 'fa-user-group' },
    { id: 'permissions', label: 'Permissions', icon: 'fa-file-lines' }
  ];

  const contentSubTabs = [
    { id: 'company', label: 'Company', icon: 'fa-building' },
    { id: 'category', label: 'Category', icon: 'fa-folder' },
    { id: 'agreement-types', label: 'Agreement Types', icon: 'fa-file-signature' },
    { id: 'status', label: 'Status', icon: 'fa-tag' }
  ];

  // Check if user has access to specific scope
  const hasScope = (scope: string): boolean => {
    if (scopesLoading) return false;
    return userScopes.includes(scope);
  };

  // Don't render anything while loading
  if (scopesLoading) {
    return null;
  }

  if (isCollapsed) {
    return (
      <>
        <div className="px-2 py-4 space-y-2">
          <button className={`w-full flex items-center justify-center p-2 rounded-lg transition-colors ${
            darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'
          }`} title="Search">
            <i className="fa-solid fa-magnifying-glass text-sm"></i>
          </button>
        </div>
        {hasScope('admin.access') && (
          <div className={`px-2 py-2 border-t space-y-1 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <button 
              className={`w-full flex items-center justify-center p-2 rounded-lg transition-colors ${
                activeTab === 'access' 
                  ? 'text-violet-600 bg-violet-50' 
                  : darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'
              }`}
              title="Access Management"
              onClick={() => onTabChange('access')}
            >
              <i className="fa-solid fa-shield-halved text-sm"></i>
            </button>
          </div>
        )}
        {hasScope('admin.content') && (
          <div className={`px-2 py-2 border-t space-y-1 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <button 
              className={`w-full flex items-center justify-center p-2 rounded-lg transition-colors ${
                activeTab === 'content' 
                  ? 'text-violet-600 bg-violet-50' 
                  : darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'
              }`}
              title="Content Management"
              onClick={() => onTabChange('content')}
            >
              <i className="fa-solid fa-folder-open text-sm"></i>
            </button>
          </div>
        )}
        {hasScope('admin.logs') && (
          <div className={`px-2 py-4 border-t space-y-1 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <button 
              className={`w-full flex items-center justify-center p-2 rounded-lg transition-colors ${
                activeTab === 'logs' 
                  ? 'text-violet-600 bg-violet-50' 
                  : darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'
              }`}
              title="Logs"
              onClick={() => onTabChange('logs')}
            >
              <i className="fa-solid fa-clock-rotate-left text-sm"></i>
            </button>
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <div className="px-4 pt-6 pb-4">
        <div className="relative">
          <i className={`fa-solid fa-magnifying-glass absolute left-3 top-3 text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}></i>
          <input 
            type="text" 
            placeholder="Search admin..." 
            className={`w-full pl-9 pr-4 py-2 text-sm border rounded-lg focus:outline-none focus:border-violet-500 ${
              darkMode
                ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                : 'border-gray-300'
            }`}
          />
        </div>
      </div>

      {/* Access Management */}
      {hasScope('admin.access') && (
        <div className={`px-4 py-2 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <button
            onClick={() => {
              setExpandedAccess(!expandedAccess);
              if (!expandedAccess) {
                onTabChange('access');
              }
            }}
            className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-lg transition-colors ${
              activeTab === 'access' 
                ? 'bg-violet-50 text-violet-600 font-medium' 
                : darkMode
                  ? 'text-gray-300 hover:bg-gray-700'
                  : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <div className="flex items-center">
              <i className={`fa-solid fa-shield-halved mr-3 text-sm ${
                activeTab === 'access' ? 'text-violet-600' : darkMode ? 'text-gray-400' : 'text-gray-500'
              }`}></i>
              <span>Access Management</span>
            </div>
            <i className={`fa-solid fa-chevron-down text-xs transition-transform ${
              expandedAccess ? 'rotate-180' : ''
            }`}></i>
          </button>
        {expandedAccess && (
          <ul className="mt-2 ml-4 space-y-1">
            {accessSubTabs.map((subTab) => (
              <li key={subTab.id}>
                <button
                  onClick={() => {
                    onTabChange('access');
                    if (onSubTabChange) {
                      onSubTabChange(subTab.id);
                    }
                  }}
                  className={`w-full flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${
                    activeTab === 'access' && activeSubTab === subTab.id
                      ? 'bg-violet-50 text-violet-600 font-medium'
                      : darkMode
                        ? 'text-gray-300 hover:bg-gray-700'
                        : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <i className={`fa-solid ${subTab.icon} mr-3 text-xs ${
                    activeTab === 'access' && activeSubTab === subTab.id 
                      ? 'text-violet-600' 
                      : darkMode ? 'text-gray-500' : 'text-gray-400'
                  }`}></i>
                  <span>{subTab.label}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
        </div>
      )}

      {/* Content Management */}
      {hasScope('admin.content') && (
        <div className={`px-4 py-2 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <button
            onClick={() => {
              setExpandedContent(!expandedContent);
              if (!expandedContent) {
                onTabChange('content');
              }
            }}
            className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-lg transition-colors ${
              activeTab === 'content' 
                ? 'bg-violet-50 text-violet-600 font-medium' 
                : darkMode
                  ? 'text-gray-300 hover:bg-gray-700'
                  : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <div className="flex items-center">
              <i className={`fa-solid fa-folder-open mr-3 text-sm ${
                activeTab === 'content' ? 'text-violet-600' : darkMode ? 'text-gray-400' : 'text-gray-500'
              }`}></i>
              <span>Content Management</span>
            </div>
            <i className={`fa-solid fa-chevron-down text-xs transition-transform ${
              expandedContent ? 'rotate-180' : ''
            }`}></i>
          </button>
        {expandedContent && (
          <ul className="mt-2 ml-4 space-y-1">
            {contentSubTabs.map((subTab) => (
              <li key={subTab.id}>
                <button
                  onClick={() => {
                    onTabChange('content');
                    if (onSubTabChange) {
                      onSubTabChange(subTab.id);
                    }
                  }}
                  className={`w-full flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${
                    activeTab === 'content' && activeSubTab === subTab.id
                      ? 'bg-violet-50 text-violet-600 font-medium'
                      : darkMode
                        ? 'text-gray-300 hover:bg-gray-700'
                        : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <i className={`fa-solid ${subTab.icon} mr-3 text-xs ${
                    activeTab === 'content' && activeSubTab === subTab.id 
                      ? 'text-violet-600' 
                      : darkMode ? 'text-gray-500' : 'text-gray-400'
                  }`}></i>
                  <span>{subTab.label}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
        </div>
      )}

      {/* Logs */}
      {hasScope('admin.logs') && (
        <div className={`px-4 py-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <button
            onClick={() => onTabChange('logs')}
            className={`w-full flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${
              activeTab === 'logs' 
                ? 'bg-violet-50 text-violet-600 font-medium' 
                : darkMode
                  ? 'text-gray-300 hover:bg-gray-700'
                  : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <i className={`fa-solid fa-clock-rotate-left mr-3 text-sm ${
              activeTab === 'logs' ? 'text-violet-600' : darkMode ? 'text-gray-400' : 'text-gray-500'
            }`}></i>
            <span>Logs</span>
          </button>
        </div>
      )}
    </>
  );
};

export default AdminSidebar;

